import type { SenderAccountType } from '@lighthouse/shared'
import {
    ClassEdge,
    FlowControlNodeType,
    type FlowEdge,
    type FlowNode,
    NODE_CARD_WIDTH,
    type NodeTypes,
    X_OFFSET,
    Y_OFFSET
} from '@lighthouse/shared'
import { nanoid } from '@lighthouse/tools'
import type { Edge, Node } from '@xyflow/react'
import { getOutgoers, useReactFlow } from '@xyflow/react'
import produce from 'immer'
import { useCallback } from 'react'

import { useFlow, useFlowContainerContext } from '@/contexts/FlowContext'

import { generateNode } from '../utils/addNode'

type HandleNodeAddPayload = {
    nodeType: NodeTypes
    prevNodeId?: string | null
    nextNodeId?: string | null
    handleId?: string
    senderEmailType?: SenderAccountType
}

const getChildrenNodeIds = (node: Node, nodes: FlowNode[], edges: Edge[]): string[] => {
    const childrenNodes: string[] = [node.id]
    const childNodes = getOutgoers<FlowNode>(node, nodes, edges)
    if (childNodes.length > 0) {
        // 添加直接子节点
        childrenNodes.push(...childNodes.map(child => child.id))
        // 递归获取每个子节点的所有后代节点
        for (const childNode of childNodes) {
            const descendants = getChildrenNodeIds(childNode, nodes, edges)
            childrenNodes.push(...descendants)
        }
        return childrenNodes
    }
    return childrenNodes
}

const getNodePositionByPrevNode = (lastOutgoer: FlowNode, prevNode: Node, zoom: number) => {
    const prevElement = document.querySelector(`div[data-id="${prevNode.id}"]`)
    const { width } = prevElement?.getBoundingClientRect() || { width: 0 }

    const nodeWidth = NODE_CARD_WIDTH > width ? NODE_CARD_WIDTH / zoom : width / zoom
    if (lastOutgoer) {
        const lastOutgoerElement = document.querySelector(`div[data-id="${lastOutgoer.id}"]`)
        const { height: lastOutgoerHeight } = lastOutgoerElement?.getBoundingClientRect() || { height: 0, width: 0 }
        const nodeHeight = lastOutgoerHeight / zoom
        return {
            x: lastOutgoer.position.x,
            y: (lastOutgoer?.position?.y ?? 0) + nodeHeight + Y_OFFSET
        }
    }

    return {
        x: (prevNode?.position?.x ?? 0) + nodeWidth + X_OFFSET,
        y: prevNode.position.y
    }
}

const getNodePositionByNextNode = (nextNode: Node, zoom: number) => {
    const nextElement = document.querySelector(`div[data-id="${nextNode.id}"]`)
    const { width } = nextElement?.getBoundingClientRect() || { width: 0 }
    const nodeWidth = width / zoom

    return {
        x: nextNode.position.x - nodeWidth - X_OFFSET,
        y: nextNode.position.y
    }
}

export const useInteraction = () => {
    const { getNodes, getEdges, getZoom } = useReactFlow()
    const { setNodes, setEdges } = useFlow()
    const { setIsChanged } = useFlowContainerContext()
    // const [deleteTarget, setDeleteTarget] = useState<SelectedNode | SelectedEdge | null>(null)

    const handleNodeAdd = useCallback(
        (payload: HandleNodeAddPayload) => {
            const { nodeType, prevNodeId, nextNodeId, handleId, senderEmailType } = payload
            // if (getNodesReadOnly()) { return }
            const nodes = getNodes() as FlowNode[]
            const edges = getEdges() as FlowEdge[]
            const zoom = getZoom()
            const nodeList: FlowNode[] = nodes.map(item => ({ ...item, selected: false }))

            if (prevNodeId && !nextNodeId) {
                const prevNodeIndex = nodeList.findIndex(node => node.id === prevNodeId)
                const prevNode = nodeList[prevNodeIndex]
                // const nodesWithSameType = nodes.filter(node => node.data.type === nodeType)
                const newNode = generateNode(nodeType, { senderEmailType, selected: true })
                const outgoers = getOutgoers<FlowNode>(prevNode, nodeList, edges).sort((a, b) => a.position.y - b.position.y)
                const lastOutgoer = outgoers[outgoers.length - 1]
                newNode.position = getNodePositionByPrevNode(lastOutgoer, prevNode, zoom)
                const newEdge: FlowEdge = {
                    id: nanoid(),
                    source: prevNodeId,
                    target: newNode.id,
                    sourceHandle: handleId,
                    type: ClassEdge.Common,
                    data: {
                        isNodeHovering: false
                    }
                }
                setNodes?.([...nodeList, newNode])
                setEdges?.([...edges, newEdge])
                setIsChanged?.(true)
                return
            }

            if (nextNodeId && !prevNodeId) {
                const nextNodeIndex = nodeList.findIndex(node => node.id === nextNodeId)
                const nextNode = nodeList[nextNodeIndex]
                const newNode = generateNode(nodeType, {
                    selected: true,
                    senderEmailType,
                    position: getNodePositionByNextNode(nextNode, zoom)
                })
                const sourceHandle =
                    newNode.data.nodeType === FlowControlNodeType.CONDITION ? newNode.data.config?.filterGroup?.[0]?.id : undefined
                const newEdge: FlowEdge = {
                    id: nanoid(),
                    source: newNode.id,
                    target: nextNodeId,
                    sourceHandle,
                    type: ClassEdge.Common,
                    data: {
                        isNodeHovering: false
                    }
                }
                setNodes?.([...nodeList, newNode])
                setEdges?.([...edges, newEdge])
                setIsChanged?.(true)
                return
            }

            if (nextNodeId && prevNodeId) {
                const nextNodeIndex = nodeList.findIndex(node => node.id === nextNodeId)
                const nextNode = nodeList[nextNodeIndex]
                // const nextElement = document.querySelector(`div[data-id="${nextNode.id}"]`)
                const newNode = generateNode(nodeType, {
                    selected: true,
                    senderEmailType,
                    position: {
                        x: nextNode.position.x,
                        y: nextNode.position.y
                    }
                })

                // const connectedEdges = getConnectedEdges([currentNode], edges)
                const childrenNodeIds = getChildrenNodeIds(nextNode, nodeList, edges)
                const newNodes = produce(nodeList, draft => {
                    draft.forEach(node => {
                        if (childrenNodeIds.includes(node.id)) {
                            node.position.x += X_OFFSET + NODE_CARD_WIDTH
                        }
                    })
                    draft.push(newNode)
                })
                const currentEdgeIndex = edges.findIndex(edge => edge.source === prevNodeId && edge.target === nextNodeId)
                const handleId = edges[currentEdgeIndex]?.sourceHandle || ''
                if (currentEdgeIndex >= 0) {
                    edges.splice(currentEdgeIndex, 1)
                }
                const newEdgesTarget: FlowEdge = {
                    id: nanoid(),
                    source: prevNodeId,
                    target: newNode.id,
                    sourceHandle: handleId,
                    type: ClassEdge.Common,
                    data: {
                        isNodeHovering: false
                    }
                }
                const newEdgesSourceHandleId =
                    newNode.data.nodeType === FlowControlNodeType.CONDITION ? newNode.data.config?.filterGroup?.[0]?.id : undefined
                const newEdgesSource: FlowEdge = {
                    id: nanoid(),
                    source: newNode.id,
                    target: nextNodeId,
                    sourceHandle: newEdgesSourceHandleId,
                    type: ClassEdge.Common,
                    data: {
                        isNodeHovering: false
                    }
                }
                setIsChanged?.(true)
                setNodes?.(newNodes)
                setEdges?.([...edges, newEdgesSource, newEdgesTarget])
            }
        },
        [getEdges, getNodes, getZoom, setEdges, setIsChanged, setNodes]
    )

    return {
        handleNodeAdd
    }
}
